import React, { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '../utils/cn';
import { CALENDLY_URL } from '../config/constants';
import { Logo } from './common/Logo';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navigation = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'Contact', href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleNavClick = (href: string) => {
    setIsMenuOpen(false);
    if (window.location.pathname !== '/') {
      window.location.assign(`/${href}`);
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        isScrolled && !isMenuOpen ? 'bg-black' : 'bg-transparent'
      )}
    >
      <div className="relative z-50 flex items-center justify-between px-6 py-5 md:px-10">
        <a href="/#home" className="flex items-center" onClick={() => setIsMenuOpen(false)}>
          <Logo width={72} height={44} />
        </a>

        <button
          className="inline-flex items-center gap-3 bg-white px-4 py-3 text-sm text-[#181615]"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
        >
          <span className="grid grid-cols-2 gap-[3px]" aria-hidden="true">
            <span className="h-1 w-1 bg-current" />
            <span className="h-1 w-1 bg-current" />
            <span className="h-1 w-1 bg-current" />
            <span className="h-1 w-1 bg-current" />
          </span>
          {isMenuOpen ? 'Close' : 'Menu'}
        </button>
      </div>

      <div
        className={cn(
          'fixed inset-0 z-40 bg-black transition-opacity duration-300',
          isMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        <div className="flex h-full flex-col justify-between px-6 pb-10 pt-28 md:px-10">
          <nav className="grid gap-1 md:grid-cols-2 md:gap-x-16">
            {navigation.map((item, index) => (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className="flex items-baseline justify-between border-b border-white/10 py-5 text-left text-4xl text-white transition-colors hover:text-[#d0d0d0] md:text-5xl"
              >
                <span>{item.name}</span>
                <span className="text-sm text-[#d0d0d0]">0{index + 1}</span>
              </button>
            ))}
          </nav>

          <div className="mt-10 flex flex-col gap-6 border-t border-white/10 pt-8 md:flex-row md:items-end md:justify-between">
            <p className="max-w-md text-sm leading-relaxed text-[#d0d0d0]">
              Web Development, Branding, App Development – All in One Studio
            </p>
            <button
              onClick={() => window.open(CALENDLY_URL, '_blank')}
              className="solid-button"
            >
              Get Started
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
